import { useEffect, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import type { PricingConfigurationInput } from '@/shared/api';
import { toFormValues, type PricingConfigurationFormValues } from './pricingConfigurationSchema';

/**
 * Ein neuer Stand vom Server ersetzt das Formular. Lag dabei ein geänderter Entwurf vor und stammt
 * die neue Version nicht aus dem eigenen Speichern, meldet der Rückgabewert diese Version – die
 * Seite sagt es, statt Eingaben still zu ersetzen. Gespeichert wird immer gegen die geladene
 * Version, nie gegen eine fremde.
 */
export function useConfigurationReset(
  form: UseFormReturn<PricingConfigurationFormValues>,
  configuration: PricingConfigurationInput,
  versionNumber: number,
  savedVersion: number | undefined,
  onDirtyChange?: (dirty: boolean) => void,
): number | null {
  const dirty = form.formState.isDirty;
  const [replacedBy, setReplacedBy] = useState<number | null>(null);
  const version = useRef(versionNumber);
  useEffect(() => {
    const foreign = versionNumber !== version.current && savedVersion !== versionNumber;
    if (form.formState.isDirty && foreign) setReplacedBy(versionNumber);
    version.current = versionNumber;
    form.reset(toFormValues(configuration));
  }, [configuration, form, savedVersion, versionNumber]);
  useEffect(() => {
    if (dirty) setReplacedBy(null);
  }, [dirty]);
  useEffect(() => onDirtyChange?.(dirty), [dirty, onDirtyChange]);
  // Verschwindet das Formular (Ladefehler, Wechsel), hängen keine offenen Änderungen mehr daran.
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  return replacedBy;
}
